import type { ICellRendererParams } from 'ag-grid-community';
import { Chip } from 'Components/atoms';
import { ACTIVE_WORKFLOWS_ROUTE } from 'Core-utils/routes';
import { ENGAGEMENT_FLOW_FINDING_KIND_URL_PARAM } from 'Core-utils/constants/constants';
import type { AgentStatusChipKind } from 'Core-utils/helpers/helpers';
import { EngagementState, FindingsInsightsDto } from '@ampsec/platform-client';
import React from 'react';
import { Link } from 'react-router-dom';

type Engagement = FindingsInsightsDto['active_engagements'][number];

/**
 * An engagement Ampy is currently driving towards the user. PENDING_VERIFICATION is part of that outreach —
 * the message has already been delivered — so it stays active, while a run held for business hours has not
 * reached the user yet.
 */
const isEngaging = (engagement: Engagement): boolean =>
    engagement.status === 'RUNNING' && engagement.state !== EngagementState.WAITING_FOR_BUSINESS_HOUR;

/**
 * An engagement that exists for the user but has not reached them yet — held for consolidation, business hours
 * or verification. The findings DTO has no explicit "awaiting" flag, so it is inferred from the engagement state
 * or a schedule that is still in the future.
 */
const isAwaitingEngagement = (engagement: Engagement): boolean => {
    if (isEngaging(engagement)) {
        return false;
    }
    const awaitingState =
        engagement.state === EngagementState.WAITING_FOR_BUSINESS_HOUR ||
        engagement.state === EngagementState.PENDING_VERIFICATION;
    if (awaitingState) {
        return true;
    }
    return !!engagement.scheduledDate && new Date(engagement.scheduledDate) > new Date();
};

/**
 * Agent status cell — blank when no agent is wired to the finding, yellow 0 when an agent exists but is not
 * engaging the user, green 0 when every agent is queued (consolidation / business hours / verification) and green
 * with the number of times Ampy has reached out while engaging, summed across the engaging agents and matching the
 * legacy `EngagementChip` reminder convention (reminders + the first outreach).
 */
const AgentStatusCellRenderer: React.FC<ICellRendererParams<FindingsInsightsDto, number>> = (params) => {
    const row = params.data;
    if (!row) {
        return null;
    }

    const engagements = row.active_engagements ?? [];
    const engaging = engagements.filter(isEngaging);
    const awaiting = engaging.length === 0 && engagements.some(isAwaitingEngagement);

    let kind: AgentStatusChipKind;
    let count = 0;
    if (engaging.length > 0) {
        kind = 'active';
        count = engaging.reduce((total, engagement) => total + (engagement.numberOfReminders ?? 0) + 1, 0);
    } else if (awaiting) {
        kind = 'awaiting';
    } else {
        kind = 'inactive';
    }

    // No agent is associated with the finding at all, so there is no status to report.
    if ((row.triggerable_agents_count ?? 0) <= 0) {
        return null;
    }

    const chip = <Chip variant="agent_status" value={`${count}`} variantValue={kind} />;

    if (!row.kind) {
        return (
            <div className="d-flex w-100 justify-content-center align-items-center h-100">
                {chip}
            </div>
        );
    }

    const engagementFlowUrl = `${ACTIVE_WORKFLOWS_ROUTE}?${ENGAGEMENT_FLOW_FINDING_KIND_URL_PARAM}=${encodeURIComponent(row.kind)}`;
    const title = `View agents triggered by this finding: ${row.kind}`;
    return (
        <div className="d-flex w-100 justify-content-center align-items-center h-100">
            <Link
                to={engagementFlowUrl}
                className="text-decoration-none ag-findings-grid__triggerable-agents-link"
                title={title}
            >
                {chip}
            </Link>
        </div>
    );
};

export default AgentStatusCellRenderer;
